/**
 * @fileoverview Guard de CONTA SWIPE — a fronteira de entrada do app.
 *
 * ===== O INVARIANTE =====
 * `hasSwipeSession()` é a fronteira de entrada, não `isAuthed`.
 *
 * `isAuthed` significa "há uma fonte Xtream resolvida neste aparelho" — é um
 * token de PROVEDOR, cunhado por `api.login()`/`/v1/auth/direct` sem conta
 * nenhuma. Ele nunca foi identidade de usuário e nunca pôde autorizar entrada:
 * quem entra por ele não tem conta, não tem cota, não tem trial e não sincroniza.
 * A conta Swipe (`hasSwipeSession()`) é a única coisa que responde "QUEM é este
 * usuário", e por isso é ela — e só ela — que abre a porta.
 *
 * ===== POR QUE ESTE COMPONENTE EXISTE =====
 * O `RootRoute` já decidia por `hasSwipeSession()` (I5+I6), mas ele governa
 * apenas a rota `/`. Todo o resto do app era decidido por outra coisa:
 *
 *  - as telas de CONFIG DE FONTE (`/onboarding/mode`, `/code`, `/login`,
 *    `/direct`, `/source/manual`) eram PÚBLICAS. Na TV empacotada isso era
 *    inofensivo (não há barra de endereço), mas o MESMO bundle serve o web em
 *    `app.swipetvplayer.com`: digitar `/code` pulava o gate e chegava ao app com
 *    ZERO conta Swipe, via `/code` → `/login` → `api.login()` → `setAuth()`;
 *  - o INTERIOR (`/home`, `/live`, `/player/:channelId`, …) era decidido só pelo
 *    `AuthGuard`, ou seja, só por `isAuthed`. E `isAuthed` é PERSISTIDO — está no
 *    `partialize` do `persist({ name: 'iptv:session' })` (store.ts). Fechar só a
 *    porta de entrada teria deixado o interior aberto PRA SEMPRE justamente pra
 *    quem já entrou por ela: com `isAuthed:true` e `iptv:tokens` no localStorage,
 *    `/home` entregava o app inteiro sem nunca mostrar o gate.
 *
 * Por isso a fronteira é UMA e vale nos dois lugares: `withSwipe` (config de
 * fonte) e `withAuth` (interior, com o `AuthGuard` aninhado por dentro).
 *
 * ===== POR QUE A LISTA É ESTA (invariante, não proxy) =====
 * O critério NÃO é "as telas que alguém reportou". É por ALLOWLIST: TUDO exige
 * conta, exceto `/` (o próprio gate), `/account/*` (como se OBTÉM uma conta) e
 * `/settings` (idioma/Sobre antes de ter uma). Guardar essas fecharia o laço em
 * cima do usuário. Rota nova nasce coberta — e o teste de invariante em
 * `routes.swipe-guard.test.tsx` falha alto se alguém abrir exceção sem escrevê-la
 * na allowlist.
 *
 * ===== O QUE ELE NÃO FAZ: DESLOGAR =====
 * Este guard NÃO chama `logout()` e NÃO apaga nada. A base instalada
 * (`isAuthed` Xtream, sem conta Swipe) é reconduzida a `/`, e lá o `RootRoute`
 * a entrega ao `AccountGateScreen` na variante `link` ("vincule sua conta") —
 * com os dados intactos, pro pareamento migrá-los via sync. Fechar a porta não
 * pode derrubar quem já estava dentro.
 *
 * E isso NÃO é carve-out pro deep link do interior: o desenho aprovado já retém
 * o legado no gate (`RootRoute` → gate variante `link`, que só oferece SAIR —
 * não existe "continuar sem vincular"). Alcançar `/home` direto não era o
 * carve-out, era fuga de uma restrição já desenhada; o interior agora manda pro
 * mesmo lugar que `/` já mandava.
 *
 * ===== DECISÃO NO RENDER, NÃO EM EFFECT =====
 * O `AuthGuard` responde "a FONTE ainda vale?", que muda DEPOIS do mount (401,
 * 403, logout) — por isso ele vive num effect e fica por DENTRO deste. Aqui a
 * pergunta é "QUEM é este usuário?", respondida na ENTRADA:
 * `hasSwipeSession()` é uma leitura síncrona de disco/memória. Decidir no render
 * e devolver `<Navigate>` significa que a tela bloqueada NUNCA monta — não
 * dispara as queries dela, não baixa o chunk lazy e, o que mais importa numa TV,
 * não chama `setFocus()` numa árvore que vai desmontar no frame seguinte
 * (setFocus em nó que morre mata o D-pad). Um effect renderizaria a tela por um
 * frame antes de expulsar.
 */

import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'

import { hasSwipeSession } from '../sync'

export function SwipeGuard({ children }: { children: ReactNode }) {
  // A fronteira. `isAuthed` NÃO entra nesta decisão de propósito: ele é o que
  // deixava a porta anônima aberta.
  if (!hasSwipeSession()) return <Navigate to="/" replace />
  return <>{children}</>
}
